import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { statusCodesMapper } from '../../src/utils/exception';
import { fetchLeadCaptureSettings } from '../api/api';
import { LeadCaptureSettingsResponse } from '../types/types';

type PostMessage = OverwriteMessage | RefetchMessage;

type RefetchMessage = {
  type: 'refetch_settings';
};

type OverwriteMessage = {
  type: 'overwrite_displayed_settings';
  data: Partial<LeadCaptureSettingsResponse>;
};

export const useLeadCaptureSettings = () => {
  const { token } = useParams<{ token: string }>();
  const [temporaryData, setTemporaryData] = useState<Partial<LeadCaptureSettingsResponse>>({});

  const {
    data: fetchedData,
    isLoading,
    isError,
    refetch,
  } = useQuery(
    ['leadCaptureSettings'],
    async () => {
      const response = await fetchLeadCaptureSettings(token);

      return statusCodesMapper(response, {
        unhandled: () => {
          throw new Error();
        },
      });
    },
    {
      retry: 2,
      staleTime: 1000 * 60 * 10,
    }
  );

  const messageHandler = (ev: MessageEvent<PostMessage>) => {
    if (ev.data?.type === 'overwrite_displayed_settings' && ev.data.data) {
      const tempData = ev.data.data;
      setTemporaryData((prev) => ({
        ...prev,
        ...(tempData?.text_fields && {
          text_fields: tempData.text_fields,
        }),
        ...(tempData?.fields_to_capture_settings && {
          fields_to_capture_settings: tempData.fields_to_capture_settings,
        }),
        ...(tempData?.redirect_url && {
          redirect_url: tempData.redirect_url,
        }),
      }));
    }
    if (ev.data?.type === 'refetch_settings') {
      refetch();
    }
  };

  useEffect(() => {
    window.addEventListener('message', messageHandler);
    return () => {
      window.removeEventListener('message', messageHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data: {
      text_fields: {
        ...fetchedData?.text_fields,
        ...temporaryData?.text_fields,
      },
      fields_to_capture_settings: {
        ...fetchedData?.fields_to_capture_settings,
        ...temporaryData?.fields_to_capture_settings,
      },
      redirect_url: fetchedData?.redirect_url,
    },
    isError,
    isLoading,
    isOverwritten: !!Object.keys(temporaryData).length,
  };
};
